<template>
  <div class="nested-item-wrapper">
    <!-- Section Rendering -->
    <div v-if="isSection(item)" class="nested-section">
      <!-- Root Level Section (Card Style) -->
      <v-card v-if="depth === 0" class="mb-4 elevation-1 rounded-lg">
        <div class="px-4 py-3 bg-gray-50 d-flex align-center">
          <v-icon color="primary" class="mr-3">mdi-folder-open</v-icon>
          <div>
            <h3 class="text-h6 font-weight-bold text-gray-800 mb-0">{{ item.sectionName }}</h3>
            <p v-if="item.description" class="text-caption text-gray-500 mb-0 mt-1">
              {{ item.description }}
            </p>
          </div>
        </div>
        <v-card-text class="px-4 py-4">
          <div class="section-body">
            <NestedQuestionRenderer
              v-for="(subItem, index) in validSubQuestions"
              :key="getItemKey(subItem, index)"
              :item="subItem"
              :responses="responses"
              :disabled="disabled"
              :depth="depth + 1"
              @update-response="onChildUpdate"
              class="mb-2"
            />
          </div>
        </v-card-text>
      </v-card>

      <!-- Nested Level Section (Tree Style) -->
      <div v-else class="nested-sub-section mb-2 ml-2">
        <div class="d-flex align-center mb-2">
          <div class="tree-connector mr-2">
            <v-icon small color="grey lighten-1">mdi-subdirectory-arrow-right</v-icon>
          </div>
          <div class="section-header-content">
            <h4 class="text-subtitle-2 font-weight-bold text-gray-700 mb-0">
              {{ item.sectionName }}
            </h4>
            <p v-if="item.description" class="text-caption text-gray-500 mb-0">
              {{ item.description }}
            </p>
          </div>
        </div>

        <div class="pl-4 ml-2 section-tree-body">
          <NestedQuestionRenderer
            v-for="(subItem, index) in validSubQuestions"
            :key="getItemKey(subItem, index)"
            :item="subItem"
            :responses="responses"
            :disabled="disabled"
            :depth="depth + 1"
            @update-response="onChildUpdate"
            class="mb-2"
          />
        </div>
      </div>
    </div>

    <!-- Question Rendering -->
    <div v-else class="question-wrapper mb-2">
      <!-- Wrap root questions in a card if they are at top level -->
      <v-card v-if="depth === 0" class="mb-4 elevation-1 rounded-lg">
        <v-card-text class="pa-0">
          <QuestionField
            :question="item"
            :disabled="disabled || item.disabled"
            :response="responses[item._id]"
            @update:response="onResponseUpdate"
          />
        </v-card-text>
      </v-card>

      <!-- Nested questions don't need a card wrapper, QuestionField has its own style -->
      <div v-else class="nested-question">
        <QuestionField
          :question="item"
          :disabled="disabled || item.disabled"
          :response="responses[item._id]"
          @update:response="onResponseUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import QuestionField from './QuestionField.vue'

export default {
  name: 'NestedQuestionRenderer',
  components: {
    QuestionField
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    responses: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    depth: {
      type: Number,
      default: 0
    }
  },
  computed: {
    validSubQuestions() {
      if (!this.item.subQuestions) return []
      return this.item.subQuestions.filter((q) => typeof q.questionType !== 'object')
    }
  },
  methods: {
    isSection(item) {
      return item.sectionName && Array.isArray(item.subQuestions)
    },
    getItemKey(item, index) {
      return item._id || item.sectionName || `item-${index}`
    },
    onResponseUpdate(value) {
      this.$emit('update-response', this.item._id, value)
    },
    onChildUpdate(id, value) {
      this.$emit('update-response', id, value)
    }
  }
}
</script>

<style scoped>
.nested-item-wrapper {
  width: 100%;
}

.border-t-4 {
  border-top-width: 4px !important;
}

.border-primary {
  border-color: var(--v-primary-base) !important;
}

.bg-gray-50 {
  background-color: #f9fafb;
}

.text-gray-800 {
  color: #1f2937;
}

.text-gray-700 {
  color: #374151;
}

.text-gray-500 {
  color: #6b7280;
}

.border-l-2 {
  border-left-width: 2px;
}

.border-gray-200 {
  border-color: #e5e7eb;
}

.border-gray-100 {
  border-color: #f3f4f6;
}

.section-tree-body {
  transition: all 0.3s ease;
}

.section-tree-body:hover {
  border-color: var(--v-primary-base);
}
</style>
